import './upsellProposals.scss';

import { SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, ButtonVariant, Collapse, Size } from '@eliberty/design-system-react';
import { Drawer } from '@eliberty/design-system-react/mantine';
import classNames from 'classnames';
import { addDays } from 'date-fns';

import defaultUpsellImage from '~assets/images/defaultUpsellImage.jpg';
import { FormattedCurrency, TextHTML } from '~atoms';
import { findModalPortal } from '~helpers';
import { UpsellProposal } from '~types/appsync.types';
import { Participant } from '~types/participant';
import { formatDateISO, parseCalendarDate } from '~utils';

export const DEFAULT_UPSELL_IMAGE: string = defaultUpsellImage;

export interface SelectedProductSummary {
  name: string;
  image?: string | null;
  validityCategoryValue?: number | null;
}

export interface UpsellProposalsProps {
  proposals: UpsellProposal[];
  currency?: string;
  date?: Date;
  participants?: Participant[];
  selectedProduct?: SelectedProductSummary;
  shopUrl?: string;
  onSelectionChange?: (proposal: UpsellProposal | null) => void;
}

const formatParticipantsSummary = (participants: Participant[]): string => {
  const counts: Record<string, number> = {};
  for (const p of participants) {
    const name = p.consumerCategory.name;
    counts[name] = (counts[name] ?? 0) + 1;
  }
  return Object.entries(counts)
    .map(([name, count]) => `${count} ${name.toLowerCase()}${count > 1 ? 's' : ''}`)
    .join(' · ');
};

const resolveImageUrl = (path: string | null | undefined, shopUrl?: string): string => {
  if (!path) return DEFAULT_UPSELL_IMAGE;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  return shopUrl ? `${shopUrl}${path}` : path;
};

const UpsellCardImage = ({
  src,
  alt,
  className,
  shopUrl,
}: {
  src?: string | null;
  alt: string;
  className: string;
  shopUrl?: string;
}) => {
  const [hasError, setHasError] = useState(false);

  const handleImageError = useCallback((e: SyntheticEvent<HTMLImageElement>) => {
    setHasError(true);
    e.currentTarget.src = DEFAULT_UPSELL_IMAGE;
  }, []);

  const imageSrc = hasError ? DEFAULT_UPSELL_IMAGE : resolveImageUrl(src, shopUrl);

  return (
    <img
      className={className}
      src={imageSrc}
      alt={alt}
      width={112}
      height={116}
      onError={handleImageError}
    />
  );
};

const UpsellProposals = ({
  proposals,
  currency,
  date,
  participants,
  selectedProduct,
  shopUrl,
  onSelectionChange,
}: UpsellProposalsProps) => {
  const intl = useIntl();
  // null = original product selected; 'slug' = that upsell card is selected/expanded
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null);
  const [detailProposal, setDetailProposal] = useState<UpsellProposal | null>(null);

  const selectedProposal = useMemo(
    () => (expandedSlug ? (proposals.find((p) => p.productSlug === expandedSlug) ?? null) : null),
    [expandedSlug, proposals],
  );

  useEffect(() => {
    onSelectionChange?.(selectedProposal);
  }, [selectedProposal, onSelectionChange]);

  const handleCardClick = useCallback((proposal: UpsellProposal) => {
    setExpandedSlug((prev) => (prev === proposal.productSlug ? null : proposal.productSlug));
  }, []);

  const handleSelectedProductClick = useCallback(() => {
    setExpandedSlug(null);
  }, []);

  const handleShowDetails = useCallback((proposal: UpsellProposal) => {
    setDetailProposal(proposal);
  }, []);

  const handleCloseDetails = useCallback(() => {
    setDetailProposal(null);
  }, []);

  const participantsSummary = useMemo(
    () =>
      participants && participants.length > 0 ? formatParticipantsSummary(participants) : null,
    [participants],
  );

  const renderPrice = (priceDifference: number, hidePriceDifference: boolean) => {
    if (hidePriceDifference || priceDifference === 0 || !currency) return null;
    return (
      <span className='UpsellProposals_cardPrice'>
        {priceDifference > 0 ? '+' : '-'}
        <FormattedCurrency value={Math.abs(priceDifference)} currency={currency} />
      </span>
    );
  };

  const renderDateRange = (proposal: UpsellProposal) => {
    const startDate = proposal.updatedFirstSkidate
      ? parseCalendarDate(proposal.updatedFirstSkidate)
      : date;
    if (!startDate || !proposal.validityCategoryValue) return null;

    const endDate = addDays(startDate, proposal.validityCategoryValue - 1);
    return `${intl.formatDate(startDate, { day: 'numeric' })} – ${intl.formatDate(endDate, { day: 'numeric', month: 'long' })}`;
  };

  const hasDateChanged = (proposal: UpsellProposal): boolean => {
    if (!date) return false;
    const isStartDateChanged =
      Boolean(proposal.updatedFirstSkidate) && proposal.updatedFirstSkidate !== formatDateISO(date);
    const isDurationChanged =
      Boolean(selectedProduct?.validityCategoryValue) &&
      proposal.validityCategoryValue !== selectedProduct?.validityCategoryValue;
    return isStartDateChanged || isDurationChanged;
  };

  const renderExpandedDetails = (proposal: UpsellProposal) => {
    const newDateRange = renderDateRange(proposal);

    return (
      <div className='UpsellProposals_expandedContent'>
        {hasDateChanged(proposal) && (
          <div className='UpsellProposals_dateWarning'>
            <span className='UpsellProposals_dateWarningIcon elibertyIcon-status-warningCircle' />
            <span className='UpsellProposals_dateWarningText'>
              <FormattedMessage
                id='upsell.dateChanged'
                defaultMessage='Your stay dates have been modified'
              />
            </span>
          </div>
        )}
        <div className='UpsellProposals_details'>
          {proposal.validityCategoryValue && (
            <div className='UpsellProposals_detailLine'>
              <span className='UpsellProposals_detailLabel'>
                <FormattedMessage id='duration' defaultMessage='Duration' />
              </span>
              <span className='UpsellProposals_detailValue'>
                {intl.formatMessage(
                  {
                    defaultMessage: '{count, plural, one {# day} other {# days}}',
                    id: 'upsell.days',
                  },
                  { count: proposal.validityCategoryValue },
                )}
              </span>
            </div>
          )}
          {newDateRange && (
            <div className='UpsellProposals_detailLine'>
              <span className='UpsellProposals_detailLabel'>
                <FormattedMessage id='dates' defaultMessage='Dates' />
              </span>
              <span className='UpsellProposals_detailValue'>{newDateRange}</span>
            </div>
          )}
          {participantsSummary && (
            <div className='UpsellProposals_detailLine'>
              <span className='UpsellProposals_detailLabel'>
                <FormattedMessage id='participants' defaultMessage='Participants' />
              </span>
              <span className='UpsellProposals_detailValue'>{participantsSummary}</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderSelectedProductDateRange = () => {
    if (!date || !selectedProduct?.validityCategoryValue) return null;
    const endDate = addDays(date, selectedProduct.validityCategoryValue - 1);
    return `${intl.formatDate(date, { day: 'numeric' })} – ${intl.formatDate(endDate, { day: 'numeric', month: 'long' })}`;
  };

  const isOriginalSelected = expandedSlug === null;

  return (
    <div className='UpsellProposals'>
      {selectedProduct && (
        <button
          type='button'
          className={classNames('UpsellProposals_selectedCard', {
            'UpsellProposals_selectedCard--active': isOriginalSelected,
          })}
          onClick={handleSelectedProductClick}
        >
          <UpsellCardImage
            src={selectedProduct.image}
            alt={selectedProduct.name}
            className='UpsellProposals_cardImage'
            shopUrl={shopUrl}
          />
          <div className='UpsellProposals_cardContent'>
            <div className='UpsellProposals_cardHeader'>
              <span className='UpsellProposals_cardTitle'>{selectedProduct.name}</span>
              <span className='UpsellProposals_includedBadge'>
                <FormattedMessage id='upsell.included' defaultMessage='Included' />
              </span>
            </div>
            <div className='UpsellProposals_details'>
              {selectedProduct.validityCategoryValue && (
                <div className='UpsellProposals_detailLine'>
                  <span className='UpsellProposals_detailLabel'>
                    <FormattedMessage id='duration' defaultMessage='Duration' />
                  </span>
                  <span className='UpsellProposals_detailValue'>
                    {intl.formatMessage(
                      {
                        defaultMessage: '{count, plural, one {# day} other {# days}}',
                        id: 'upsell.days',
                      },
                      { count: selectedProduct.validityCategoryValue },
                    )}
                  </span>
                </div>
              )}
              {(() => {
                const dateRange = renderSelectedProductDateRange();
                return dateRange ? (
                  <div className='UpsellProposals_detailLine'>
                    <span className='UpsellProposals_detailLabel'>
                      <FormattedMessage id='date' defaultMessage='Date' />
                    </span>
                    <span className='UpsellProposals_detailValue'>{dateRange}</span>
                  </div>
                ) : null;
              })()}
              {participantsSummary && (
                <div className='UpsellProposals_detailLine'>
                  <span className='UpsellProposals_detailLabel'>
                    <FormattedMessage id='participants' defaultMessage='Participants' />
                  </span>
                  <span className='UpsellProposals_detailValue'>{participantsSummary}</span>
                </div>
              )}
            </div>
          </div>
        </button>
      )}
      <p className='UpsellProposals_title'>
        <FormattedMessage id='upsell.title' defaultMessage='Upgrade your pass' />
      </p>
      <ul className='UpsellProposals_list'>
        {proposals.map((proposal) => {
          const isExpanded = expandedSlug === proposal.productSlug;
          return (
            <li key={proposal.productSlug} className='UpsellProposals_item'>
              <div
                className={classNames('UpsellProposals_card', {
                  'UpsellProposals_card-expanded': isExpanded,
                })}
              >
                {/* Top row: clickable toggle */}
                <button
                  type='button'
                  className='UpsellProposals_cardToggle'
                  onClick={() => handleCardClick(proposal)}
                >
                  <div className='UpsellProposals_cardTop'>
                    <UpsellCardImage
                      src={proposal.image}
                      alt={proposal.title}
                      className='UpsellProposals_cardImage'
                      shopUrl={shopUrl}
                    />
                    <div className='UpsellProposals_cardContent'>
                      <div className='UpsellProposals_cardHeader'>
                        <span className='UpsellProposals_cardTitle'>{proposal.title}</span>
                        {renderPrice(proposal.priceDifference, proposal.hidePriceDifference)}
                      </div>
                      {/* Unselected: description to the right of image */}
                      {!isExpanded && proposal.description && (
                        <div className='UpsellProposals_cardDescription UpsellProposals_cardDescription-clamped'>
                          <TextHTML text={proposal.description} />
                        </div>
                      )}
                      {/* Selected: summary details to the right of image */}
                      {isExpanded && renderExpandedDetails(proposal)}
                    </div>
                  </div>
                </button>
                {/* Collapsible expanded section: description below + "Learn more" */}
                <Collapse isOpen={isExpanded}>
                  <div className='UpsellProposals_collapseWrapper'>
                    {proposal.description && (
                      <div className='UpsellProposals_cardDescription UpsellProposals_cardDescription-clamped'>
                        <TextHTML text={proposal.description} />
                      </div>
                    )}
                    <Button
                      variant={ButtonVariant.Ghost}
                      type='button'
                      size={Size.S}
                      onClick={() => handleShowDetails(proposal)}
                    >
                      <FormattedMessage id='upsell.moreInfo' defaultMessage='Learn more' />
                    </Button>
                  </div>
                </Collapse>
              </div>
            </li>
          );
        })}
      </ul>

      <Drawer
        opened={detailProposal !== null}
        onClose={handleCloseDetails}
        position='right'
        size='lg'
        withCloseButton={false}
        portalProps={{ target: findModalPortal() }}
        classNames={{ body: 'UpsellProposals_drawerBody' }}
      >
        {detailProposal && (
          <div className='UpsellProposals_drawer'>
            <UpsellCardImage
              src={detailProposal.image}
              alt={detailProposal.title}
              className='UpsellProposals_drawerImage'
              shopUrl={shopUrl}
            />
            <div className='UpsellProposals_drawerContent'>
              <h2 className='UpsellProposals_drawerTitle'>{detailProposal.title}</h2>
              {detailProposal.description && (
                <div className='UpsellProposals_drawerDescription'>
                  <TextHTML text={detailProposal.description} />
                </div>
              )}
            </div>
            <div className='UpsellProposals_drawerFooter'>
              <button
                type='button'
                className='UpsellProposals_drawerClose'
                onClick={handleCloseDetails}
              >
                <FormattedMessage id='close' defaultMessage='Close' />
              </button>
            </div>
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default UpsellProposals;
